import { simplifyCoordinates } from '../utils/math';
import { VectorRender } from "./Render";
import { contains } from "../geotools";
export var FillStyle;
(function (FillStyle) {
    FillStyle[FillStyle["None"] = 0] = "None";
    FillStyle[FillStyle["Color"] = 1] = "Color";
    FillStyle[FillStyle["Image"] = 2] = "Image";
})(FillStyle || (FillStyle = {}));
/**
 * Rendered polygon
 * @alias sGis.render.Poly
 */
export class PolyRender extends VectorRender {
    /**
     * @param coordinates - the coordinates of the polygon.
     * @param options - properties to be assigned to the instance
     */
    constructor(coordinates, options = {}) {
        super();
        /** Whether the first and the last points should be connected. */
        this.enclosed = false;
        /** Fill style of the polygon. */
        this.fillStyle = FillStyle.Color;
        /** Stroke color of the polygon. Can be any valid css color string. */
        this.strokeColor = 'black';
        /** Fill color of the polygon. Can be any valid css color string. */
        this.fillColor = 'transparent';
        /** Stroke width of the polyline. */
        this.strokeWidth = 1;
        /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */
        this.ignoreEvents = false;
        /** The distance (px) from the drawn line inside which the event is still considered to be inside the line. */
        this.lineContainsTolerance = 4;
        /** Dash pattern for the line as specified in HTML CanvasRenderingContext2D.setLineDash() specification. */
        this.lineDash = [];
        /** Fill image of the polygon */
        this.fillImage = null;
        /** Drop shadow of the polygon {offsetX, offsetY, blur, color}. */
        this.shadow = null;
        /**
         * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
         *
         * - `"butt"`
         *      The ends of lines are squared off at the endpoints.
         * - `"round"`
         *      The ends of lines are rounded.
         * - `"square"`
         *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
         */
        this.lineCap = "round";
        /**
         * Property of the Canvas 2D API determines the shape used to join two line segments where they meet.
         *
         * - `"bevel"`
         *      Fills an additional triangular area between the common endpoint of connected segments, and the separate outside rectangular corners of each segment.
         * - `"round"`
         *      Rounds off the corners of a shape by filling an additional sector of disc centered at the common endpoint of connected segments. The radius for these rounded corners is equal to the line width.
         * - `"miter"`
         *      Connected segments are joined by extending their outside edges to connect at a single point, with the effect of filling an additional lozenge-shaped area. This setting is affected by the miterLimit property
         */
        this.lineJoin = "round";
        /**
         * Property of the Canvas 2D API sets the miter limit ratio.
         * @docs https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/miterLimit
         */
        this.miterLimit = 10;
        /** Drawn stroke outside. */
        this.isOutsideStroke = false;
        /** Drawn stroke inside. */
        this.isInsideStroke = false;
        Object.assign(this, options);
        this.coordinates = coordinates;
    }
    get isVector() { return true; }
    contains(position) {
        let polygonContains = contains(this.coordinates, position, this.strokeWidth / 2 + this.lineContainsTolerance);
        if (this.enclosed)
            return !!polygonContains;
        return Array.isArray(polygonContains) && polygonContains[1] !== this.coordinates[polygonContains[0]].length - 1;
    }
    setupRender(ctx, domPainter) {
        if (this.mapCoordinates && domPainter) {
            const xScale = domPainter.getXScale();
            const yScale = domPainter.getYScale();
            this.renderPosition = simplifyCoordinates(this.mapCoordinates.map(ring => {
                return ring.map(point => {
                    return [xScale(point[0]), domPainter.height - yScale(point[1])];
                });
            }), this.strokeWidth);
        }
        ctx.lineCap = this.lineCap;
        ctx.lineJoin = this.lineJoin;
        ctx.miterLimit = this.miterLimit;
        ctx.lineWidth = this.strokeWidth;
        ctx.strokeStyle = this.strokeWidth <= 0 ? "transparent" : this.strokeColor;
        if (this.strokeWidth <= 0) {
            ctx.strokeStyle = "transparent";
        }
        else if (typeof this.strokeColor === "object") {
            const { values } = this.strokeColor;
            const gradient = ctx.createLinearGradient(0, 0, ctx.canvas.width, 0);
            values.forEach((item, index) => {
                gradient.addColorStop(index / (values.length - 1), item.value);
            });
            ctx.strokeStyle = gradient;
        }
        else {
            ctx.strokeStyle = this.strokeColor;
        }
        ctx.setLineDash(this.lineDash || []);
    }
    ;
    _drawLines(ctx) {
        const coordinates = this.renderPosition || this.coordinates;
        for (let i = 0, ringsCount = coordinates.length; i < ringsCount; i++) {
            const ring = coordinates[i];
            ctx.moveTo(ring[0][0], ring[0][1]);
            for (let j = 1, len = ring.length; j < len; j++) {
                ctx.lineTo(ring[j][0], ring[j][1]);
            }
            if (this.enclosed) {
                ctx.closePath();
            }
        }
    }
    _resetShadow(ctx) {
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'rgba(0, 0, 0, 0)';
    }
    _setShadow(ctx, reset) {
        if (reset || this.shadow === null) {
            this._resetShadow(ctx);
        }
        else {
            const { offsetX, offsetY, blur, color, isOuter } = this.shadow;
            ctx.shadowOffsetX = offsetX;
            ctx.shadowOffsetY = offsetY;
            ctx.shadowBlur = blur;
            ctx.shadowColor = color;
            if (isOuter) {
                ctx.fillStyle = color;
                ctx.fill();
                this._resetShadow(ctx);
                ctx.fillStyle = 'rgba(0, 0, 0, 1)';
                ctx.globalCompositeOperation = 'destination-out';
                ctx.fill();
                ctx.fillStyle = this.fillColor;
                ctx.globalCompositeOperation = 'source-over';
            }
        }
    }
    renderObject(ctx, domPainter) {
        const coordinates = this.renderPosition || this.coordinates;
        ctx.beginPath();
        this._drawLines(ctx);
        this._setShadow(ctx);
        if (this.isOutsideStroke) {
            ctx.lineWidth = this.strokeWidth * 2;
            ctx.stroke();
            ctx.globalCompositeOperation = "destination-out";
            ctx.fillStyle = "#000";
            ctx.fill();
            ctx.globalCompositeOperation = 'source-over';
        }
        if (this.fillStyle === FillStyle.Color) {
            ctx.fillStyle = this.fillColor;
            ctx.fill();
        }
        else if (this.fillStyle === FillStyle.Image) {
            ctx.fillStyle = ctx.createPattern(this.fillImage, 'repeat');
            const patternOffsetX = (coordinates[0][0][0]) % this.fillImage.width, patternOffsetY = (coordinates[0][0][1]) % this.fillImage.height;
            ctx.translate(patternOffsetX, patternOffsetY);
            ctx.fill();
            ctx.translate(-patternOffsetX, -patternOffsetY);
        }
        if (this.enclosed) {
            this._setShadow(ctx, true);
        }
        if (this.isInsideStroke) {
            ctx.lineWidth = this.strokeWidth * 2;
            ctx.clip();
            ctx.stroke();
        }
        if (!this.isOutsideStroke && !this.isInsideStroke) {
            ctx.stroke();
        }
    }
    ;
}
